import { reactive } from 'vue'
import { v4 as uuid } from 'uuid'
import dateFormat from 'dateformat'
import pick from 'lodash/pick'
import {
  createCollection,
  deleteCollection,
  loadContentCollections,
  loadSharedContentCollection,
  addEntitiesToCollection,
  removeEntityFromCollection,
  getCollectionById,
  updateCollection as updateCollectionPackage,
} from '@onxmaps/content-collections-web'

import markupService from '@/services/markup-service'
import { removeShareGrantForEntity } from '@/services/content-sharing-service'
import { isContentCollectionsPackageActive } from '@/services/content-collection-service/helpers'

import { log } from '@/services/logging-service'
import { httpGetJson, httpPostJson } from '@/lib/net'
import httpClient from '@/lib/http-client'
import { getUserId } from '@/services/identity-client'
import {
  ERROR_DUPLICATE_MESSAGE,
  apiConfig,
} from '@/services/content-collection-service/constants'

/**
 * Service for requesting, modifying, storing the state of customer's collections
 */
class ContentCollectionService {
  /**
   * Initialization function
   */
  constructor() {
    this.collections = {}
    this.deletedCollections = []
    this.markupsWithinCollections = new Map()
    this.isLoading = false
    this.lastRefreshTime = undefined
  }

  /**
   * Request collections, but only when none are loaded. This will re-request each call if the
   * account has no collections.
   */
  async initialRequestCollections() {
    if (
      !this.collections.hasOwnProperty('fish') ||
      this.collections['fish'].length === 0
    ) {
      performance.mark('start_content_collection_load')
      await this.requestCollections()
      performance.mark('end_content_collection_load')
      performance.measure(
        'time_for_content_collection_load',
        'start_content_collection_load',
        'end_content_collection_load'
      )
    }
  }

  /**
   * Request current list of collections from API
   */
  async requestCollections(type) {
    this.isLoading = true

    let collections
    if (isContentCollectionsPackageActive.value) {
      collections = await loadContentCollections(httpClient, {
        collectionType: this.transformType(type),
      })
    } else {
      collections = await httpGetJson(
        `${apiConfig.CONTENT_COLLECTIONS}?includeEntities=true&type=${this.transformType(type)}`
      )
    }
    this.setCollections(collections, type)
    this.isLoading = false
    this.lastRefreshTime = dateFormat(Date.now(), 'h:MM TT')
  }

  /**
   * Request a shared collection by share ID. Used when previewing shared content.
   *
   * @param {string} shareId - ID of share to look up
   *
   * @return {ContentCollection} Shared collection
   */
  async requestSharedCollectionByShareId(shareId) {
    this.isLoading = true

    let collection
    if (isContentCollectionsPackageActive.value) {
      collection = await loadSharedContentCollection(httpClient, { shareId })
    } else {
      collection = await httpGetJson(apiConfig.SHARED_COLLECTION(shareId))
    }

    this.isLoading = false
    return collection
  }

  /**
   * Set collections state
   *
   * @param {array} collections - List of collections for customer
   */
  setCollections(collections = [], type) {
    // set collections to only active collections
    this.collections[this.transformType(type)] = this._sortByUpdatedDesc(
      collections.filter((c) => !c.deleted_at)
    )

    this.setMarkupsInCollections()

    // save deleted collections separately, in case we need to reference them
    this.deletedCollections = this._sortByUpdatedDesc(
      collections.filter((c) => c.deleted_at)
    )
  }

  /**
   * Create a data structure to look up whether a markup is currently in a collection or not
   */
  setMarkupsInCollections() {
    this.getAllCollections().forEach((collection) => {
      collection.entities.forEach((entity) => {
        this._addMarkupInCollection(entity.uuid, collection.uuid)
      })
    })
  }

  /**
   * Updates the markupsWithinCollections data structure to indicate that a markup is in the given collection.
   * @param {string} entityId - Markup ID
   * @param {string} collectionId - Collection ID
   */
  _addMarkupInCollection(entityId, collectionId) {
    if (!this.markupsWithinCollections.has(entityId)) {
      // no entry for markup exists already
      this.markupsWithinCollections.set(entityId, new Set([collectionId]))
    } else {
      // entry already exists, add this collection to it
      this.markupsWithinCollections.get(entityId).add(collectionId)
    }
  }

  /**
   * Get the state of the collections list for type
   *
   * @return {array} List of undeleted collections
   */
  getCollections(type) {
    if (this.collections.hasOwnProperty(this.transformType(type))) {
      return this.collections[this.transformType(type)]
    }
    return []
  }

  /**
   * Get the state of all collections
   */
  getAllCollections() {
    return Object.keys(this.collections).length > 0
      ? Object.values(this.collections).flat()
      : []
  }

  /**
   * Get the state of the deleted collections list
   *
   * @return {array} List of deleted collections
   */
  getDeletedCollections() {
    return this.deletedCollections
  }

  /**
   * Look up a collection by ID
   *
   * @param {string} id - ID of collection to retrieve
   * @return {object} collection object
   */
  getCollectionByID(id) {
    return this.getAllCollections().find((c) => c.uuid === id)
  }

  /**
   * Add new collection
   *
   * @param {object} toAdd - Collection to add
   * @return {string|false} UUID of created collection
   */
  async addCollection(toAdd) {
    if (!toAdd.name) {
      return false
    }
    const newCollection = { ...toAdd }
    newCollection.uuid = uuid()
    newCollection.entities = []
    newCollection.created_at = newCollection.updated_at = new Date()
    newCollection.created_by = await getUserId()
    newCollection.permissions = ['collection.add_entity', 'collection.delete']
    newCollection.type = this.transformType(toAdd.type)

    try {
      if (isContentCollectionsPackageActive.value) {
        const collectionUuid = await createCollection(httpClient, {
          collection: {
            name: newCollection.name,
            type: newCollection.type,
            notes: newCollection.notes,
            userId: newCollection.created_by,
          },
        })

        const packageCollection = getCollectionById(collectionUuid)
        const typeCollections = this.getCollections(toAdd.type)
        typeCollections.unshift(packageCollection)
        this.setCollections(typeCollections, toAdd.type)

        return collectionUuid
      }

      await httpPostJson(apiConfig.CREATE_CONTENT_COLLECTION, newCollection)

      const typeCollections = this.getCollections(toAdd.type)
      typeCollections.unshift(newCollection)
      this.setCollections(typeCollections, toAdd.type)

      return newCollection.uuid
    } catch (e) {
      return ''
    }
  }

  /**
   * Converts a webmap type into a content collection backend service type
   *
   * @param {String} type - Webmap type
   * @return {String} Returns backend service type
   */
  transformType(type) {
    if (type) {
      // example:  fish_user_group
      return `fish_${type}`
    }
    // base type falls back to app i.e. "hunt" or "offroad"
    return 'fish'
  }

  /**
   * Update collection
   *
   * @param {String} uuid - Collection UUID to update
   * @param {Object} [changes] - Properties to update. May be null or undefined to only update the timestamp.
   * @return {Object} Returns the updated collection
   */
  async updateCollection(uuid, changes) {
    if (!uuid) {
      throw new Error('Missing collection uuid')
    }

    const collection = this.getAllCollections().find((c) => c.uuid === uuid)
    if (!collection) {
      throw new Error(`Collection with uuid "${uuid}" not found.`)
    }

    // Initialize changes (if not passed in) and update timestamp
    changes ??= {}
    changes.updated_at = new Date()

    // Craft request body. Use current collection object as basis, and apply supported overridden fields from data.
    const props = ['name', 'notes', 'updated_at']
    const body = Object.assign(
      {},
      pick(collection, props.concat('uuid')),
      pick(changes, props)
    )

    try {
      if (isContentCollectionsPackageActive.value) {
        await updateCollectionPackage(httpClient, {
          collectionId: uuid,
          metadata: {
            updated_at: body.updated_at.toISOString(),
            name: body.name,
            notes: body.notes,
          },
        })
      } else {
        await httpPostJson(apiConfig.UPDATE_CONTENT_COLLECTION_METADATA, body)
      }
      Object.assign(collection, body)
    } catch (e) {
      return null
    }

    this.collections[collection.type] = this._sortByUpdatedDesc(
      this.collections[collection.type]
    )
    return collection
  }

  /**
   * Delete collection
   *
   * @param {string} uuid - Collection ID to delete
   * @return {string} UUID of collection, empty if error
   */
  async deleteCollection(uuid) {
    const toDelete = this.getAllCollections().find((c) => c.uuid === uuid)
    if (toDelete === undefined) {
      throw new Error(`Collection with uuid "${uuid}" not found`)
    }

    try {
      if (isContentCollectionsPackageActive.value) {
        await deleteCollection(httpClient, { id: uuid })
      } else {
        await httpPostJson(apiConfig.DELETE_CONTENT_COLLECTION, {
          uuid,
        })
      }
      const index = this.collections[toDelete.type].findIndex(
        (c) => c.uuid === toDelete.uuid
      )
      if (index !== -1) {
        this.collections[toDelete.type].splice(index, 1)
      }
      return uuid
    } catch (e) {
      return ''
    }
  }

  /**
   * Remove shared collection
   *
   * @param {string} uuid - Collection ID to remove
   * @return {string} UUID of collection, empty if error
   */
  async removeSharedCollection(uuid) {
    const toDelete = this.getAllCollections().find((c) => c.uuid === uuid)
    if (toDelete === undefined) {
      throw new Error(`Collection with uuid "${uuid}" not found`)
    }

    const collectionEntities = toDelete?.entities
    const entityIds = collectionEntities?.map((e) => e.uuid)

    await removeShareGrantForEntity(uuid, 'collection', await getUserId())
    // remove the markups from the map, the share service will asynchronously remove
    // the markup share grants
    markupService.removeMarkups(entityIds)
    const index = this.collections[toDelete.type].findIndex(
      (c) => c.uuid === toDelete.uuid
    )
    if (index !== -1) {
      this.collections[toDelete.type].splice(index, 1)
    }

    return uuid
  }

  /**
   * Get entities for a collection
   *
   * @param {string} id - ID of collection to get entities for
   * @param {boolean} includeGeo - Whether to include the geometry in the returned data
   * return {array|null} List of (fully hydrated) entities for the collection
   */
  getEntitiesForCollection(id, includeGeo = false) {
    const collection = this.getCollectionByID(id)
    if (collection) {
      const collectionEntities = collection?.entities
        .map((entity) => {
          return markupService.getMarkupByID(entity?.uuid, includeGeo)
        })
        .filter((markup) => markup?.uuid)

      if (collection?.entities?.length > collectionEntities?.length) {
        // this should only happen in the case that the backend markup service has
        // not caught up with processing the access grant pubsub events from the
        // sharing service. If we see a lot of these we may want to try markup here (much later)
        log('collection_markups_not_all_available', {
          collection_id: id,
        })
      }

      return collectionEntities
    }
  }

  /**
   * Determine if the newly created collection was populated with markups.
   *
   * @param {string} id - Collection uuid
   * @return {bool} - Return true if collection empty, false otherwise.
   */
  isCollectionEmpty(id) {
    return this.getEntitiesForCollection(id).length === 0
  }

  /**
   * Get the bounding box for the collection
   *
   * @param {string} id of collection
   * @returns {Array} list of coordinates
   */
  getCollectionBounds(id) {
    // above a couple hundred entities, calculating the bounding box gets expensive
    const BOUNDS_CALCULATION_CUTOFF = 500

    const collectionEntities = this.getEntitiesForCollection(id, true)
    if (
      !collectionEntities ||
      collectionEntities.length === 0 ||
      collectionEntities.length > BOUNDS_CALCULATION_CUTOFF
    ) {
      return
    }

    return markupService.getMarkupArrayBounds(collectionEntities)
  }

  /**
   * Add entities to a collection. Only updates the list once at the
   * end of all the requests.
   *
   * @param {(array|object)} oneOrManyMarkups - List of markups to add to the collection
   * @param {string} collectionId - ID of the collection to add entities to
   * @return {array} List of errors that occurred during add requests
   */
  async addToCollection(oneOrManyMarkups, collectionId) {
    // Coerce everything into an array so this function can handle one or many
    const markups = [oneOrManyMarkups].flat()
    const entities = []
    const errors = []

    // check if the markups are already in the collection
    // if they are, add them to the errors array
    // if they are not, add them to the entities array for body of the API request
    markups.forEach((m) => {
      if (this.isMarkupInCollection(m.uuid, collectionId)) {
        errors.push({ uuid: m.uuid, message: ERROR_DUPLICATE_MESSAGE })
      } else {
        entities.push({
          uuid: m.uuid,
          type: m.type,
          inserted_at: new Date(),
        })
      }
    })

    if (isContentCollectionsPackageActive.value) {
      try {
        await addEntitiesToCollection(httpClient, {
          collectionId,
          entities,
        })

        const currentCollection = this.getCollectionByID(collectionId)
        if (currentCollection) {
          // Reassign to trigger reactivity (entities already added by package)
          currentCollection.entities = [...currentCollection.entities]
          currentCollection.updated_at = new Date().toISOString()
          this.collections[currentCollection.type] = this._sortByUpdatedDesc(
            this.collections[currentCollection.type]
          )
        }
      } catch (e) {
        entities.length > 1
          ? errors.push({ uuid: 'multiple', message: e })
          : errors.push({ uuid: entities[0].uuid, message: e })
      }
    } else {
      let apiPath = apiConfig.ADD_ENTITY
      const body = {
        uuid: collectionId,
      }
      // if there are no entities to add, return errors
      if (!entities.length) {
        return errors
        // if there is only one entity, add it to the API body with entity key
      } else if (entities.length === 1) {
        body.entity = entities[0]
        await this._postEntitiesToCollection(apiPath, body, errors)
        // if there are multiple entities, use the add_entities endpoint
        // and add them to the API body with entities key
      } else if (entities.length > 1 && entities.length < 1000) {
        apiPath = apiConfig.ADD_ENTITIES
        body.entities = entities
        await this._postEntitiesToCollection(apiPath, body, errors)
        // if there are 1000 or more entities, we need to chunk the API requests
        // and send them in batches of 1000
      } else if (entities.length >= 1000) {
        apiPath = apiConfig.ADD_ENTITIES
        const batches = []
        for (let i = 0; i < entities.length; i += 1000) {
          batches.push(entities.slice(i, i + 1000))
        }
        batches.forEach(async (batch) => {
          const batchBody = { uuid: collectionId, entities: batch }
          await this._postEntitiesToCollection(apiPath, batchBody, errors)
        })
      }

      // add new entities to collection all at once
      const currentCollection = this.getCollectionByID(collectionId)
      if (currentCollection) {
        const existingEntities = currentCollection.entities?.slice() ?? []
        currentCollection.entities = [...existingEntities, ...entities]
        currentCollection.updated_at = new Date().toISOString()

        this.collections[currentCollection.type] = this._sortByUpdatedDesc(
          this.collections[currentCollection.type]
        )
      }
    }

    // Update the markupsWithinCollections data structure
    for (const markup of markups) {
      this._addMarkupInCollection(markup.uuid, collectionId)
    }

    return errors
  }

  /**
   * Post entities to a collection
   * FUTURE: this function can be removed when isContentCollectionsPackageActive is removed
   * @param {string} apiPath - API path to post to (add_entity or add_entities)
   * @param {object} body - Body of the request containing the collection ID and entities
   * @param {array} errors - Array to push errors to
   */
  async _postEntitiesToCollection(apiPath, body, errors) {
    try {
      await httpPostJson(apiPath, body)
    } catch (e) {
      apiPath === apiConfig.ADD_ENTITIES
        ? errors.push({ uuid: 'multiple', message: e })
        : errors.push({ uuid: body.entity.uuid, message: e })
    }
  }

  /**
   * Remove an entity from a collection
   *
   * @param {string} contentID - ID of content to remove from the collection
   * @param {string} type - Type of content you are removing
   * @param {string} collectionId - ID of collection remove content from
   * @return {boolean} Whether or not entity was successfully removed
   */
  async removeFromCollection(contentID, type, collectionId) {
    const entity = {
      uuid: contentID,
      type: type,
      removed_at: new Date(),
    }

    try {
      if (isContentCollectionsPackageActive.value) {
        await removeEntityFromCollection(httpClient, {
          entity,
          collectionId,
        })

        const currentCollection = this.getCollectionByID(collectionId)
        if (currentCollection) {
          currentCollection.updated_at = new Date().toISOString()
          this.collections[currentCollection.type] = this._sortByUpdatedDesc(
            this.collections[currentCollection.type]
          )
        }
      } else {
        const body = {
          uuid: collectionId,
          entity,
        }
        await httpPostJson(apiConfig.REMOVE_ENTITY, body)
        await this.updateCollection(collectionId)
      }

      this._removeEntity(contentID, collectionId)
      return true
    } catch (e) {
      return false
    }
  }

  /**
   * Remove entity from collection locally (instead of reloading from API)
   *
   * @param {string} entityID - ID of content to remove
   * @param {string} collectionId - ID of collection to remove from
   */
  _removeEntity(entityID, collectionId) {
    const collection = this.getCollectionByID(collectionId)
    const newEntities = collection.entities.filter((e) => e.uuid !== entityID)
    if (this.markupsWithinCollections.has(entityID)) {
      this.markupsWithinCollections.get(entityID).delete(collectionId)
    }
    collection.entities = newEntities
  }

  /**
   * Sort helper function to sort a list of collections by updated date descending
   *
   * @param {Array} collections - Collection array to sort
   * @return {Array} - items sorted by newest to oldest
   */
  _sortByUpdatedDesc(collections) {
    return collections
      .slice()
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
  }

  /**
   * Get collections containing markup with given markup ID
   *
   * @param {string} markupID - ID of markup to filter collections by
   * @return {array} - List of collections containing the given markup.
   */
  getCollectionsForMarkup(markupID) {
    const collectionIds = this.markupsWithinCollections.get(markupID)
    return this.getCollections().filter((c) => collectionIds?.has(c.uuid))
  }

  /**
   * Lookup whether a markup is in a collection or not
   *
   * @param {string} uuid - ID of markup to look up
   * @return {boolean} Whether this markup is in a collection
   */
  isMarkupInACollection(uuid) {
    return this.markupsWithinCollections.has(uuid)
  }

  /**
   * Lookup whether a markup is in a shared collection or not
   *
   * @param {string} uuid - ID of markup to look up
   * @param {string} userId - ID of current user
   * @return {boolean} Whether markup is in a share collection
   */
  isMarkupInASharedCollection(uuid, userId) {
    const collectionIds = this.markupsWithinCollections.get(uuid)
    return this.getCollections().some((c) => {
      return collectionIds?.has(c.uuid) && c.created_by !== userId
    })
  }

  /**
   * Determines if the given markup is part of the given collection
   * @param {String} markupID - uuid of the markup
   * @param {String} collectionID - uuid of the collection
   */
  isMarkupInCollection(markupID, collectionID) {
    const markupsInCollection = this.getCollectionByID(collectionID)?.entities
    return markupsInCollection?.some((entity) => entity.uuid === markupID)
  }
}
export default reactive(new ContentCollectionService())
